import { Claims } from '@keyliving/shared-types';
import { isTokenExpired } from '@keyliving/utils';
import { captureException } from '@sentry/react';
import { ReactElement, useCallback, useEffect, useState } from 'react';

import { useAppDispatch } from '../hooks/redux';
import useAuth from '../hooks/useAuth';
import {
    setAuthDetails,
    setCurrentUser,
    useLazyMeQuery,
    useLazyVerifyAuthTokenQuery,
} from '../redux/modules/auth';
import FullScreenFallbackLayout from './layout/FullScreenFallbackLayout';

interface HydrationGateProps {
    children: ReactElement | null;
}

export default function HydrationGate({ children }: HydrationGateProps) {
    const { token: authToken } = useAuth();
    const [canMount, setCanMount] = useState<boolean>(() => {
        // If we don't have an authToken, we aren't logged in. So mounth and let protected routes do their thing
        return authToken === null;
    });
    const [verifyAuthToken] = useLazyVerifyAuthTokenQuery();
    const [getMe] = useLazyMeQuery();
    const dispatch = useAppDispatch();

    const verifyToken = useCallback(
        async (token: string): Promise<Claims> => {
            try {
                // Verify the token...
                const claims = await verifyAuthToken(token).unwrap();

                // ...token expiry should be handled by the api, but to be safe...
                const isExpired = isTokenExpired(claims.exp);

                if (isExpired) {
                    throw new Error('Token expired');
                }

                return Promise.resolve(claims);
            } catch (error) {
                return Promise.reject('Token has expired');
            }
        },
        [verifyAuthToken]
    );

    const hydrateStore = useCallback(
        async (authToken: string) => {
            try {
                const tokensToVerify = [verifyToken(authToken)];
                const impersonationToken = sessionStorage.getItem('impersonationToken');

                if (impersonationToken) {
                    tokensToVerify.push(verifyToken(impersonationToken));
                }

                const [authUserClaims, impersonationClaims] = await Promise.all(tokensToVerify);

                let claims = authUserClaims;
                let token = authToken;

                if (impersonationClaims && impersonationToken) {
                    const adminId = authUserClaims.id;
                    const impersonatorId = impersonationClaims.act?.sub;

                    /**
                     * We've tried to use a token that we aren't authenticated to use
                     */
                    if (!impersonatorId || impersonatorId !== adminId) {
                        /**
                         * Maybe a low chance this is helpful but might alert us if someone
                         * is trying to do something nefarious.
                         */
                        captureException(
                            new Error(`[Impersonation]: Unauthorized to impersonate user`),
                            {
                                tags: {
                                    page: 'Impersonation',
                                },
                                extra: {
                                    // User who is logged in via auth cookie
                                    adminUserId: adminId,
                                    // The "actor" on the impersonation token
                                    impersonatorId,
                                    // User the impersonation token is for
                                    impersonatedUserId: impersonationClaims.id,
                                },
                            }
                        );

                        throw new Error(
                            'The actor user.id on the impersonation token does not match the user.id of the person who is trying to impersonate them'
                        );
                    }

                    /**
                     * If we are impersonating a user, we set our state to the impersonated users
                     * token and claims.
                     */
                    claims = impersonationClaims;
                    token = impersonationToken;
                }

                dispatch(
                    setAuthDetails({
                        claims,
                        token,
                        user: null,
                    })
                );

                const user = await getMe().unwrap();
                dispatch(setCurrentUser(user));
                setCanMount(true);
            } catch (unknown) {
                /**
                 * Just swallow the error for now. When the app mounts it will
                 * redirect to the /login route without a user
                 */
                setCanMount(true);
            }
        },
        [dispatch, getMe, verifyToken]
    );

    useEffect(() => {
        if (authToken) {
            hydrateStore(authToken);
        }

        // Only once on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!canMount) {
        return <FullScreenFallbackLayout isLoading />;
    }

    return <>{children}</>;
}
